import { Box, Button, Typography } from '@mui/material';
import { toast } from '@side/pantry';
import { useEffect, useRef } from 'react';
import { navigateTo } from '../../utils/navigate';

// Lightweight admin-ui route whose only job is to prove IAP + Okta both
// authenticated, then postMessage success and close. Kept in sync with
// admin-ui src/app/iap-bootstrap/page.tsx.
const IAP_BOOTSTRAP_PATH = '/admin/iap-bootstrap';
const SUCCESS_MESSAGE = 'side-iap-auth:success';
const SUCCESS_CHANNEL = 'side-iap-auth';

type EmbeddedLoginProps = {
  /** Same-origin admin-ui path to load once auth succeeds, e.g. /admin/users/123 */
  destination: string;
};

/**
 * Auth prompt shown when identity-ui /login renders inside the Zendesk embed
 * iframe with an IAP-gated (/admin) destination.
 *
 * admin-ui sits behind Google IAP, and both IAP's Google login and Okta's login
 * refuse to be framed (X-Frame-Options), so we can't authenticate inside the
 * cross-site iframe. Instead we open a top-level popup that runs Okta then IAP
 * (/identity/okta-login -> /redirect -> /admin/iap-bootstrap), establishing both
 * cookies on this origin. Ordering Okta first means the session already exists
 * by the time the popup reaches /admin, so admin-ui's only redirect is IAP's own
 * (Google), which returns to the bootstrap page rather than bouncing away.
 *
 * When the bootstrap popup signals success we navigate the iframe to the real
 * destination, which now renders with both cookies in place.
 *
 * Note: the embedded iframe is cross-site to the Zendesk top-level page, so the
 * (Google-managed, non-partitionable) IAP cookie only reaches admin-ui while the
 * browser still sends SameSite=None third-party cookies. See PR for the
 * longer-term options.
 */
// [iap-embed-debug] Client-side trail for the popup auth handshake. The flow
// spans Zendesk → identity-ui iframe → popup → admin-ui and has failed in prod
// twice in ways only diagnosable from logs — keep until the embed is GA'd.
// biome-ignore-start lint/suspicious/noConsole: embed debug logging
function debugLog(event: string, details?: Record<string, unknown>) {
  console.log(`[iap-embed-debug] EmbeddedLogin ${event}`, details ?? {});
}
// biome-ignore-end lint/suspicious/noConsole: embed debug logging

export function EmbeddedLogin({ destination }: EmbeddedLoginProps) {
  const popupRef = useRef<Window | null>(null);

  useEffect(() => {
    debugLog('mounted', {
      destination,
      framed: window.self !== window.top,
      origin: window.location.origin,
    });
    const onSuccess = (source: string) => {
      // The popup may already be unreachable/un-closable: our pages send
      // Cross-Origin-Opener-Policy: same-origin, so the popup's cross-origin
      // hops (Okta, IAP's Google login) switch its browsing context group and
      // disconnect the window proxy. close() is best-effort; the bootstrap
      // page shows its own "you can close this window" fallback.
      try {
        popupRef.current?.close();
      } catch {
        // disconnected proxy — ignore
      }
      // Navigate the iframe to admin-ui; the IAP + session cookies now exist.
      debugLog('auth success — navigating iframe', { source, destination });
      navigateTo(destination);
    };

    // PRIMARY signal: BroadcastChannel. Same-origin delivery between the
    // bootstrap popup and this iframe that survives COOP — window.opener is
    // severed by the popup's cross-origin auth hops (see above), so an
    // opener-postMessage-only handshake never arrives. Channel name shared
    // with admin-ui src/app/iap-bootstrap/page.tsx.
    const channel =
      typeof BroadcastChannel !== 'undefined'
        ? new BroadcastChannel(SUCCESS_CHANNEL)
        : null;
    if (channel) {
      channel.onmessage = (event: MessageEvent) => {
        debugLog('broadcast received', { type: event.data?.type ?? null });
        if (event.data?.type === SUCCESS_MESSAGE) {
          onSuccess('broadcast-channel');
        }
      };
    } else {
      debugLog('BroadcastChannel unavailable — relying on opener postMessage');
    }

    // FALLBACK signal: opener postMessage (works only if the opener
    // relationship somehow survived, e.g. COOP relaxed in the future).
    const onMessage = (event: MessageEvent) => {
      // The popup and bootstrap page are same-origin as this page.
      if (event.origin !== window.location.origin) {
        return;
      }
      debugLog('message received', {
        type: event.data?.type ?? null,
        matchesSuccess: event.data?.type === SUCCESS_MESSAGE,
      });
      if (event.data?.type === SUCCESS_MESSAGE) {
        onSuccess('opener-postmessage');
      }
    };
    window.addEventListener('message', onMessage);
    return () => {
      channel?.close();
      window.removeEventListener('message', onMessage);
      // Close an orphaned popup if we unmount before it signals success
      // (e.g. the user navigates away from /login).
      try {
        popupRef.current?.close();
      } catch {
        // disconnected proxy — ignore
      }
    };
  }, [destination]);

  const handleLogin = () => {
    const popupUrl = `${window.location.origin}/identity/okta-login?destination=${encodeURIComponent(
      IAP_BOOTSTRAP_PATH,
    )}`;
    // Must run from this click (a user gesture) or the browser blocks the popup.
    popupRef.current = window.open(
      popupUrl,
      'side-iap-auth',
      'popup,width=480,height=640',
    );
    debugLog('popup opened', { popupUrl, blocked: !popupRef.current });
    if (!popupRef.current) {
      toast('error', 'Pop-up blocked. Allow pop-ups for Zendesk, then retry.');
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        height: '100vh',
        p: 3,
        textAlign: 'center',
      }}
    >
      <Typography variant="body1">
        Sign in to Side to view this customer.
      </Typography>
      <Button variant="contained" onClick={handleLogin}>
        Sign in to Side
      </Button>
    </Box>
  );
}
